import { Button, cn, Dialog, Input, Logo, Spinner, Wordmark } from "@qn/ui";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  Contact,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Settings,
  Shapes,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useProjects } from "../lib/agents";
import { api, type Conversation, type User } from "../lib/api";
import { useFeature } from "../lib/features";
import { groupConversations } from "../lib/group";
import { useT } from "../lib/i18n";
import { NamedIcon } from "./NamedIcon";
import { NotificationBell } from "./NotificationBell";
import { QuickToggles } from "./QuickToggles";

export function Sidebar({ user, onNavigate }: { user: User; onNavigate: () => void }) {
  const t = useT();
  const assistantOn = useFeature("assistant");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { id: activeId } = useParams();
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [rename, setRename] = useState<Conversation | null>(null);
  const [del, setDel] = useState<Conversation | null>(null);
  const projects = useProjects().data?.projects ?? [];
  const [newProject, setNewProject] = useState(false);

  const list = useQuery({
    queryKey: ["conversations", { q, showArchived }],
    queryFn: () =>
      api<{ conversations: Conversation[] }>(
        `/conversations?archived=${showArchived ? 1 : 0}&q=${encodeURIComponent(q)}`,
      ),
    placeholderData: (prev) => prev,
  });
  const patch = useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string } & Partial<Pick<Conversation, "title" | "pinned" | "archived">>) =>
      api(`/conversations/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/conversations/${id}`, { method: "DELETE" }),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      if (id === activeId) navigate("/");
    },
  });

  const groups = groupConversations(list.data?.conversations ?? []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-4 pb-3 pt-4">
        <Logo size={30} />
        <Wordmark by />
        <span className="ms-auto flex items-center">
          <QuickToggles />
          <NotificationBell onNavigate={onNavigate} />
        </span>
      </div>

      <div className="space-y-2 px-3">
        <Button
          variant="primary"
          className="w-full justify-start"
          onClick={() => {
            navigate("/");
            onNavigate();
          }}
        >
          <Plus className="size-4" /> {t("chat.new")}
        </Button>
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-[var(--fg-faint)]" />
          <Input
            className="ps-9"
            placeholder={t("sidebar.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t("sidebar.search")}
          />
        </div>
      </div>

      <NavLink
        to="/artifacts"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "mx-3 mt-2 flex items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--fg-muted)] hover:bg-[var(--surface-3)] hover:text-[var(--fg)]",
            isActive && "bg-[var(--surface-3)] text-[var(--fg)]",
          )
        }
      >
        <Shapes className="size-4" /> {t("nav.artifacts")}
      </NavLink>
      {assistantOn && (
        <NavLink
          to="/assistant"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "mx-3 mt-1 flex items-center gap-2.5 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--fg-muted)] hover:bg-[var(--surface-3)] hover:text-[var(--fg)]",
              isActive && "bg-[var(--surface-3)] text-[var(--fg)]",
            )
          }
        >
          <Contact className="size-4" /> {t("nav.assistant")}
        </NavLink>
      )}
      <div className="mx-3 mt-3">
        <div className="flex items-center justify-between px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-faint)]">
          {t("nav.projects")}
          <button
            type="button"
            aria-label={t("projects.new")}
            onClick={() => setNewProject(true)}
            className="grid size-7 place-items-center rounded-md hover:bg-[var(--surface-3)]"
          >
            <Plus className="size-4" />
          </button>
        </div>
        {projects.slice(0, 6).map((p) => (
          <NavLink
            key={p.id}
            to={`/p/${p.id}`}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 truncate rounded-[var(--radius-sm)] px-3 py-1.5 text-sm text-[var(--fg-muted)] hover:bg-[var(--surface-3)] hover:text-[var(--fg)]",
                isActive && "bg-[var(--surface-3)] text-[var(--fg)]",
              )
            }
          >
            <NamedIcon
              name={p.icon}
              fallback="folder"
              tile={false}
              className="size-4 shrink-0 text-[var(--fg-faint)]"
            />
            <span className="truncate" dir="auto">
              {p.name}
            </span>
          </NavLink>
        ))}
      </div>
      <nav className="mt-3 flex-1 overflow-y-auto px-2 pb-2" aria-label={t("sidebar.history")}>
        {list.isLoading && (
          <div className="space-y-2 px-3 py-4" aria-busy="true">
            {[78, 62, 90, 55, 70].map((w) => (
              <div
                key={w}
                className="h-8 animate-pulse rounded-lg bg-[var(--surface-3)]"
                style={{ width: `${w}%` }}
              />
            ))}
          </div>
        )}
        {!list.isLoading && groups.length === 0 && (
          <p className="px-3 py-8 text-center text-sm text-[var(--fg-faint)]">
            {q
              ? t("sidebar.noResults")
              : showArchived
                ? t("sidebar.noArchived")
                : t("sidebar.empty")}
          </p>
        )}
        {groups.map((g) => (
          <section key={g.key} className="mb-3">
            <h3 className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wider text-[var(--fg-faint)]">
              {t(`group.${g.key}`)}
            </h3>
            {g.items.map((c) => (
              <div key={c.id} className="group relative">
                <NavLink
                  to={`/c/${c.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "block truncate rounded-[var(--radius-sm)] px-3 py-[calc(0.5rem*var(--density))] pe-9 text-sm text-[var(--fg-muted)] transition hover:bg-[var(--surface-3)] hover:text-[var(--fg)]",
                      isActive && "bg-[var(--surface-3)] text-[var(--fg)]",
                    )
                  }
                  dir="auto"
                >
                  {c.title}
                </NavLink>
                <Dropdown.Root>
                  <Dropdown.Trigger asChild>
                    <button
                      type="button"
                      aria-label={t("sidebar.options")}
                      className="absolute end-1.5 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-md text-[var(--fg-faint)] opacity-0 transition hover:bg-[var(--line)] hover:text-[var(--fg)] focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 max-lg:opacity-100"
                    >
                      <MoreHorizontal className="size-4" />
                    </button>
                  </Dropdown.Trigger>
                  <Dropdown.Portal>
                    <Dropdown.Content
                      align="end"
                      className="glass z-50 min-w-44 rounded-xl border border-[var(--line)] p-1.5 text-sm shadow-[var(--shadow)]"
                    >
                      <MenuItem icon={<Pencil />} onSelect={() => setRename(c)}>
                        {t("action.rename")}
                      </MenuItem>
                      <MenuItem
                        icon={c.pinned ? <PinOff /> : <Pin />}
                        onSelect={() => patch.mutate({ id: c.id, pinned: !c.pinned })}
                      >
                        {c.pinned ? t("action.unpin") : t("action.pin")}
                      </MenuItem>
                      <MenuItem
                        icon={c.archived ? <ArchiveRestore /> : <Archive />}
                        onSelect={() => patch.mutate({ id: c.id, archived: !c.archived })}
                      >
                        {c.archived ? t("action.unarchive") : t("action.archive")}
                      </MenuItem>
                      <MenuItem icon={<Trash2 />} danger onSelect={() => setDel(c)}>
                        {t("action.delete")}
                      </MenuItem>
                    </Dropdown.Content>
                  </Dropdown.Portal>
                </Dropdown.Root>
              </div>
            ))}
          </section>
        ))}
      </nav>

      <div className="border-t border-[var(--line)] p-2">
        <button
          type="button"
          className="mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-[var(--fg-faint)] hover:text-[var(--fg-muted)]"
          onClick={() => setShowArchived((v) => !v)}
        >
          <Archive className="size-3.5" />{" "}
          {showArchived ? t("sidebar.hideArchived") : t("sidebar.showArchived")}
        </button>
        <NavLink
          to="/settings"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-sm hover:bg-[var(--surface-3)]",
              isActive && "bg-[var(--surface-3)]",
            )
          }
        >
          <span className="grid size-8 place-items-center rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--accent-fg)]">
            {user.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{user.name}</span>
            <span className="block truncate text-xs text-[var(--fg-faint)]">
              {t("sidebar.settings")}
            </span>
          </span>
          <Settings className="size-4 text-[var(--fg-faint)]" />
        </NavLink>
      </div>

      <Dialog open={newProject} onOpenChange={setNewProject} title={t("projects.new")}>
        <RenameForm
          initial=""
          onSubmit={async (name) => {
            const r = await api<{ project: { id: string } }>("/projects", {
              method: "POST",
              body: { name },
            });
            await qc.invalidateQueries({ queryKey: ["projects"] });
            setNewProject(false);
            navigate(`/p/${r.project.id}`);
            onNavigate();
          }}
        />
      </Dialog>
      <Dialog
        open={!!rename}
        onOpenChange={(o) => !o && setRename(null)}
        title={t("action.rename")}
      >
        {rename && (
          <RenameForm
            initial={rename.title}
            onSubmit={(title) => {
              patch.mutate({ id: rename.id, title });
              setRename(null);
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t("sidebar.deleteTitle")}
        description={t("sidebar.deleteBody")}
      >
        <div className="flex justify-end gap-2">
          <Button onClick={() => setDel(null)}>{t("action.cancel")}</Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (del) remove.mutate(del.id);
              setDel(null);
            }}
          >
            {t("action.delete")}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function MenuItem({
  children,
  icon,
  onSelect,
  danger,
}: {
  children: string;
  icon: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
}) {
  return (
    <Dropdown.Item
      onSelect={onSelect}
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-[var(--surface-3)] [&_svg]:size-4",
        danger ? "text-[var(--danger)]" : "text-[var(--fg)]",
      )}
    >
      {icon}
      {children}
    </Dropdown.Item>
  );
}

function RenameForm({ initial, onSubmit }: { initial: string; onSubmit: (t: string) => void }) {
  const t = useT();
  const [v, setV] = useState(initial);
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onSubmit(v.trim());
      }}
    >
      <Input
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        maxLength={200}
        dir="auto"
      />
      <div className="flex justify-end">
        <Button type="submit" variant="primary">
          {t("action.save")}
        </Button>
      </div>
    </form>
  );
}
